import React, { useState, useEffect, useRef, useCallback, Fragment, useMemo } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { dashboardToast } from "../../utils/dashboardToast";
import "bootstrap/dist/css/bootstrap.min.css";

import Menu from "../components/menu";
import DashboardShell from "../../components/DashboardShell";
import { useMenuState } from "../../Common/MenuStateContext";
import SaListPageShell from "../../components/sa/SaListPageShell";
import ContentLoader from "../../components/ContentLoader";
import RecordDetailModal from "../../components/RecordDetailModal";
import TableSortSelect from "../../components/TableSortSelect";
import { Alert, Button as UiButton, Pagination as UiPagination } from "../../components/ui";
import { TABLE_SORT, sortTableRows } from "../../utils/tableSort";
import { getSubSectors, addOrUpdateSubSector, deleteSubSector } from "../../services/CostEstimationsetupApi";
import { ChevronDown, ChevronRight, Factory, Pencil, PieChart, Plus, Search, Trash2 } from "lucide-react";
import "../../assets/css/dashboard.css";

const EmptyCell = () => <span className="app-td__empty">—</span>;

const SectorManagement = () => {
  const { isMenuCollapsed, toggleMenuCollapse } = useMenuState();
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewRecord, setViewRecord] = useState(null);
  const [viewMode, setViewMode] = useState("parent"); // "parent" | "child"
  const [sectorToDelete, setSectorToDelete] = useState(null);
  const [formData, setFormData] = useState({
    id: 0,
    industryId: "",
    name: "",
    code: "",
    isActive: true,
    subSectors: [] // Sub-sectors linked to the same industry
  });

  // Store original sub-sectors to track deletions
  const [originalSubSectors, setOriginalSubSectors] = useState([]);

  // Industries data - will be extracted from API response
  const [industries, setIndustries] = useState([]);

  // Sectors data - will be populated from API
  const [sectors, setSectors] = useState([]);

  // Sub Sectors data - will be populated from API
  const [subSectors, setSubSectors] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set()); // Track expanded sector rows
  const [subSectorsLoading, setSubSectorsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortKey, setSortKey] = useState(TABLE_SORT.DATE_DESC);

  // Pagination state for subsectors
  const [subSectorPagination, setSubSectorPagination] = useState({
    pageNumber: 1,
    pageSize: 100,
    totalPages: 1,
    totalRecords: 0,
    hasNextPage: false,
    hasPreviousPage: false
  });

  const handleMenuSelection = useCallback((item) => {
    setSelectedItem(item);
    return item;
  }, []);

  // Function to fetch sub-sectors from API
  const fetchSubSectorsFromAPI = useCallback(async (industryId, searchTerm = '', isActive = true, page = 1, pageSize = 100, sort = sortKey) => {
    try {
      setSubSectorsLoading(true);

      const response = await getSubSectors({
        industryId,
        searchTerm,
        isActive,
        page,
        pageSize,
        sort,
      });

      // Handle the response structure: { data: { data: [...], totalRecords: ..., pageNumber: ..., pageSize: ..., totalPages: ..., hasNextPage: ..., hasPreviousPage: ... }, succeeded: true, code: 0, message: "" }
      if (response && response.succeeded && response.data && response.data.data) {
        const subSectorsData = response.data.data.map(item => ({
          id: item.id,
          industryId: item.industryId,
          industryName: item.industryName,
          name: item.name,
          code: item.code,
          isActive: item.isActive
        }));

        // Update pagination state
        setSubSectorPagination({
          pageNumber: response.data.pageNumber,
          pageSize: response.data.pageSize,
          totalPages: response.data.totalPages,
          totalRecords: response.data.totalRecords,
          hasNextPage: response.data.hasNextPage,
          hasPreviousPage: response.data.hasPreviousPage
        });

        return subSectorsData;
      } else if (response && response.data && Array.isArray(response.data)) {
        // Fallback for different response structure
        return response.data;
      } else if (Array.isArray(response)) {
        // Direct array response
        return response;
      }

      console.warn('Unexpected response structure:', response);
      return [];
    } catch (error) {
      console.error('Error fetching sub-sectors from API:', error);
      dashboardToast.error(`Failed to fetch sub-sectors from server: ${error.message}`);
      return [];
    } finally {
      setSubSectorsLoading(false);
    }
  }, [sortKey]);

  // Function to extract unique industries from sub-sectors data
  const extractIndustriesFromSubSectors = useCallback((subSectorsData) => {
    const industryMap = new Map();

    subSectorsData.forEach(subSector => {
      const industryId = subSector.industryId;
      const industryName = subSector.industryName;

      if (!industryMap.has(industryId)) {
        // Generate a code from the industry name (first letters of each word)
        const code = industryName
          .split(' ')
          .map(word => word.charAt(0).toUpperCase())
          .join('')
          .substring(0, 4); // Limit to 4 characters

        industryMap.set(industryId, {
          id: industryId,
          name: industryName,
          code: code
        });
      }
    });

    return Array.from(industryMap.values());
  }, []);

  // Function to load initial data and create sectors from industries
  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);

      // Fetch sub-sectors from multiple industry IDs to get all industries
      // Based on your API response, we have industries with IDs 1, 2, 4, 5, 6, 7
      const industryIdsToCheck = [1, 2, 4, 5, 6, 7];
      let allSubSectors = [];

      // Fetch sub-sectors from each industry to build complete industry list
      for (const industryId of industryIdsToCheck) {
        try {
          const subSectors = await fetchSubSectorsFromAPI(industryId, '', true, 1, 100);
          if (subSectors.length > 0) {
            allSubSectors = [...allSubSectors, ...subSectors];
          }
        } catch (error) {
          console.warn(`Failed to fetch sub-sectors for industry ${industryId}:`, error);
          // Continue with other industries even if one fails
        }
      }

      if (allSubSectors.length > 0) {
        // Extract unique industries from the sub-sectors data
        const extractedIndustries = extractIndustriesFromSubSectors(allSubSectors);
        setIndustries(extractedIndustries);

        // Create sectors from industries - each industry becomes a sector
        const sectorsFromIndustries = extractedIndustries.map(industry => ({
          id: industry.id,
          industryId: industry.id,
          name: industry.name,
          code: industry.code,
          isActive: true
        }));

        setSectors(sectorsFromIndustries);
      } else {
        console.warn('No sub-sectors found to extract industries');
        setError('No data available to load industries');
      }

    } catch (error) {
      console.error('Error loading initial data:', error);
      setError('Failed to load initial data');
    } finally {
      setLoading(false);
    }
  }, [fetchSubSectorsFromAPI, extractIndustriesFromSubSectors]);

  const [apiPageInfo, setApiPageInfo] = useState({
    pageNumber: 1,
    pageSize: 100,
    totalPages: 1,
    totalCount: 0,
  });

  const handleShow = useCallback(async (sector = null) => {
    if (sector) {
      // Load sub-sectors for this industry when editing
      const industrySubSectors = await fetchSubSectorsFromAPI(sector.industryId, '', true, 1, 100);

      const subSectorsData = industrySubSectors.map(sub => ({
        id: sub.id,
        name: sub.name,
        code: sub.code,
        isActive: sub.isActive
      }));

      setFormData({
        id: sector.id,
        industryId: sector.industryId || "",
        name: sector.name || "",
        code: sector.code || "",
        isActive: sector.isActive !== undefined ? sector.isActive : true,
        subSectors: subSectorsData // Load actual sub-sectors from API
      });

      // Store original sub-sectors to track deletions
      setOriginalSubSectors(subSectorsData);
    } else {
      setFormData({
        id: 0,
        industryId: "",
        name: "",
        code: "",
        isActive: true,
        subSectors: []
      });
      setOriginalSubSectors([]);
    }
    setShowModal(true);
  }, [fetchSubSectorsFromAPI]);

  const handleClose = useCallback(() => {
    setShowModal(false);
    setFormData({
      id: 0,
      industryId: "",
      name: "",
      code: "",
      isActive: true,
      subSectors: []
    });
    setOriginalSubSectors([]);
  }, []);

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  }, []);

  const handleAddSubSectorField = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      subSectors: [...prev.subSectors, { name: "", code: "" }]
    }));
  }, []);

  const handleRemoveSubSectorField = useCallback((index) => {
    setFormData(prev => {
      const newSubSectors = [...prev.subSectors];
      newSubSectors.splice(index, 1);
      return { ...prev, subSectors: newSubSectors };
    });
  }, []);

  const handleSubSectorFieldChange = useCallback((index, field, value) => {
    setFormData(prev => ({
      ...prev,
      subSectors: prev.subSectors.map((subSector, i) =>
        i === index ? { ...subSector, [field]: value } : subSector
      )
    }));
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const { industryId, name, code, subSectors } = formData;
      if (industryId && name && code) {
        try {
          if (formData.id === 0) {
            // Create new sector
            const newSector = {
              id: Date.now(),
              industryId: parseInt(industryId),
              name: name,
              code: code.toUpperCase(),
              isActive: formData.isActive,
              subSectors: formData.subSectors || []
            };
            setSectors(prev => [...prev, newSector]);

            // Create sub-sectors using API if any
            if (subSectors && subSectors.length > 0) {
              const validSubSectors = subSectors.filter(ss => ss.name && ss.code);

              if (validSubSectors.length > 0) {
                // Create each sub-sector using the API
                const apiPromises = validSubSectors.map(async (ss) => {
                  try {
                    const subSectorData = {
                      id: 0, // New sub-sector
                      industryId: parseInt(industryId),
                      name: ss.name.trim(),
                      code: ss.code.toUpperCase().trim(),
                      isActive: ss.isActive !== undefined ? ss.isActive : true
                    };

                    await addOrUpdateSubSector(subSectorData);

                    return {
                      id: response.data?.id || Date.now() + Math.random(),
                      industryId: parseInt(industryId),
                      name: ss.name,
                      code: ss.code.toUpperCase(),
                      isActive: ss.isActive !== undefined ? ss.isActive : true
                    };
                  } catch (error) {
                    console.error('Failed to create sub-sector via API:', error);
                    dashboardToast.error(`Failed to create sub-sector "${ss.name}": ${error.message}`);
                    return null;
                  }
                });

                const createdSubSectors = (await Promise.all(apiPromises)).filter(Boolean);

                if (createdSubSectors.length > 0) {
                  setSubSectors(prev => [...prev, ...createdSubSectors]);
                  dashboardToast.success(`Sector created successfully with ${createdSubSectors.length} sub-sector(s)`);
                } else {
                  dashboardToast.success("Sector created successfully (sub-sectors failed to create)");
                }
              } else {
                dashboardToast.success("Sector created successfully");
              }
            } else {
              dashboardToast.success("Sector created successfully");
            }
          } else {
            // Update existing sector
            setSectors(prev =>
              prev.map(sector =>
                sector.id === formData.id
                  ? {
                    ...sector,
                    industryId: parseInt(industryId),
                    name,
                    code: code.toUpperCase(),
                    isActive: formData.isActive,
                    subSectors: formData.subSectors || []
                  }
                  : sector
              )
            );

            // Update sub-sectors using API
            const validSubSectors = subSectors ? subSectors.filter(ss => ss.name && ss.code) : [];

            // Identify sub-sectors that were deleted (exist in original but not in current form)
            const currentSubSectorIds = validSubSectors.map(ss => ss.id).filter(Boolean);
            const deletedSubSectors = originalSubSectors.filter(original =>
              original.id && !currentSubSectorIds.includes(original.id)
            );

            // Delete removed sub-sectors via API
            if (deletedSubSectors.length > 0) {
              const deletePromises = deletedSubSectors.map(async (deletedSub) => {
                try {
                  await deleteSubSector(deletedSub.id);
                  return deletedSub.id;
                } catch (error) {
                  console.error('Failed to delete sub-sector via API:', error);
                  dashboardToast.error(`Failed to delete sub-sector "${deletedSub.name}": ${error.message}`);
                  return null;
                }
              });

              await Promise.all(deletePromises);
            }

            // Add/Update remaining sub-sectors
            if (validSubSectors.length > 0) {
              const apiPromises = validSubSectors.map(async (ss) => {
                try {
                  const subSectorData = {
                    id: ss.id || 0, // Use existing ID or 0 for new
                    industryId: parseInt(industryId),
                    name: ss.name.trim(),
                    code: ss.code.toUpperCase().trim(),
                    isActive: ss.isActive !== undefined ? ss.isActive : true
                  };

                  await addOrUpdateSubSector(subSectorData);

                  return {
                    id: response.data?.id || ss.id || Date.now() + Math.random(),
                    industryId: parseInt(industryId),
                    name: ss.name,
                    code: ss.code.toUpperCase(),
                    isActive: ss.isActive !== undefined ? ss.isActive : true
                  };
                } catch (error) {
                  console.error('Failed to update sub-sector via API:', error);
                  dashboardToast.error(`Failed to update sub-sector "${ss.name}": ${error.message}`);
                  return null;
                }
              });

              const updatedSubSectors = (await Promise.all(apiPromises)).filter(Boolean);

              // Update local state - remove all existing sub-sectors for this industry and add updated ones
              setSubSectors(prev => {
                const filtered = prev.filter(sub => sub.industryId !== parseInt(industryId));
                return [...filtered, ...updatedSubSectors];
              });

              const deletedCount = deletedSubSectors.length;
              const updatedCount = updatedSubSectors.length;

              if (deletedCount > 0 && updatedCount > 0) {
                dashboardToast.success(`Sector updated successfully: ${updatedCount} sub-sector(s) updated, ${deletedCount} deleted`);
              } else if (deletedCount > 0) {
                dashboardToast.success(`Sector updated successfully: ${deletedCount} sub-sector(s) deleted`);
              } else if (updatedCount > 0) {
                dashboardToast.success(`Sector updated successfully: ${updatedCount} sub-sector(s) updated`);
              } else {
                dashboardToast.success("Sector updated successfully");
              }
            } else {
              // If no valid sub-sectors, remove all existing ones for this industry
              setSubSectors(prev => prev.filter(sub => sub.industryId !== parseInt(industryId)));
              const deletedCount = deletedSubSectors.length;
              if (deletedCount > 0) {
                dashboardToast.success(`Sector updated successfully: ${deletedCount} sub-sector(s) deleted`);
              } else {
                dashboardToast.success("Sector updated successfully");
              }
            }
          }
          handleClose();
        } catch (err) {
          console.error(err);
          dashboardToast.error("Failed to save sector");
        }
      } else {
        dashboardToast.error("Please fill in all required fields");
      }
    },
    [formData, handleClose]
  );

  const handleDelete = useCallback((sector) => {
    setSectorToDelete(sector);
    setShowDeleteModal(true);
  }, []);

  const confirmDelete = useCallback(() => {
    try {
      // Check if it's a sector with sub-sectors
      if (sectorToDelete.subSectors && sectorToDelete.subSectors.length > 0) {
        // Delete sector and all related sub-sectors
        setSectors(prev => prev.filter(sector => sector.id !== sectorToDelete.id));
        setSubSectors(prev => prev.filter(sub => sub.industryId !== sectorToDelete.industryId));
        // Remove from expanded rows
        setExpandedRows(prev => {
          const newSet = new Set(prev);
          newSet.delete(sectorToDelete.id);
          return newSet;
        });
        dashboardToast.success("Sector and related Sub Sectors deleted successfully");
      } else {
        // Delete sector only
        setSectors(prev => prev.filter(sector => sector.id !== sectorToDelete.id));
        dashboardToast.success("Sector deleted successfully");
      }
    } catch (err) {
      console.error(err);
      dashboardToast.error("Failed to delete sector");
    } finally {
      setShowDeleteModal(false);
      setSectorToDelete(null);
    }
  }, [sectorToDelete]);

  const cancelDelete = useCallback(() => {
    setShowDeleteModal(false);
    setSectorToDelete(null);
  }, []);

  const openViewModal = useCallback((record, mode = "parent") => {
    setViewRecord(record);
    setViewMode(mode);
    setShowViewModal(true);
  }, []);

  const closeViewModal = useCallback(() => {
    setShowViewModal(false);
    setViewRecord(null);
    setViewMode("parent");
  }, []);

  const goToPage = useCallback(n => {
    const pg = Math.min(Math.max(n, 1), apiPageInfo.totalPages);
    if (pg !== apiPageInfo.pageNumber) {
      setApiPageInfo(prev => ({ ...prev, pageNumber: pg }));
    }
  }, [apiPageInfo.pageNumber, apiPageInfo.totalPages]);

  // Pagination functions for subsectors
  const goToSubSectorPage = useCallback(async (pageNumber) => {
    const pg = Math.min(Math.max(pageNumber, 1), subSectorPagination.totalPages);
    if (pg !== subSectorPagination.pageNumber) {
      setSubSectorPagination(prev => ({ ...prev, pageNumber: pg }));

      // Fetch subsectors for all expanded industries with new page
      const expandedIndustryIds = Array.from(expandedRows).map(sectorId => {
        const sector = sectors.find(s => s.id === sectorId);
        return sector ? sector.industryId : null;
      }).filter(Boolean);

      for (const industryId of expandedIndustryIds) {
        const apiSubSectors = await fetchSubSectorsFromAPI(industryId, searchTerm, true, pg, subSectorPagination.pageSize);
        if (apiSubSectors.length > 0) {
          setSubSectors(prev => {
            const filtered = prev.filter(sub => sub.industryId !== industryId);
            return [...filtered, ...apiSubSectors];
          });
        }
      }
    }
  }, [subSectorPagination.pageNumber, subSectorPagination.totalPages, subSectorPagination.pageSize, expandedRows, sectors, searchTerm, fetchSubSectorsFromAPI]);

  // Load initial data when component mounts
  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // Calculate pagination info based on visible sectors
  const displayedSectors = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    const filtered = query
      ? sectors.filter((sector) =>
          (sector.name || "").toLowerCase().includes(query) ||
          (sector.code || "").toLowerCase().includes(query)
        )
      : sectors;
    return sortTableRows(filtered, sortKey, {
      name: (row) => row.name,
      date: (row) => row.id,
    });
  }, [sectors, searchTerm, sortKey]);

  useEffect(() => {
    const totalCount = displayedSectors.length;
    const totalPages = Math.max(1, Math.ceil(totalCount / apiPageInfo.pageSize) || 1);
    setApiPageInfo(prev => ({
      ...prev,
      totalPages,
      totalCount,
      pageNumber: Math.min(prev.pageNumber, totalPages)
    }));
  }, [displayedSectors, apiPageInfo.pageSize]);

  // Get paginated sectors
  const getPaginatedSectors = () => {
    const startIndex = (apiPageInfo.pageNumber - 1) * apiPageInfo.pageSize;
    const endIndex = startIndex + apiPageInfo.pageSize;
    return displayedSectors.slice(startIndex, endIndex);
  };

  // Get industry name by ID
  const getIndustryName = (industryId) => {
    const industry = industries.find(ind => ind.id === industryId);
    return industry ? industry.name : 'Unknown';
  };

  // Get sub-sectors for a specific industry from the state
  const getSubSectorsForIndustry = (industryId) => {
    return subSectors.filter(sub => sub.industryId === industryId);
  };

  // Group sub-sectors by industry
  const getGroupedSubSectors = () => {
    const grouped = {};
    subSectors.forEach(subSector => {
      const industryId = subSector.industryId;
      if (!grouped[industryId]) {
        grouped[industryId] = {
          industryId: industryId,
          industryName: subSector.industryName || getIndustryName(industryId),
          subSectors: []
        };
      }
      grouped[industryId].subSectors.push(subSector);
    });
    return Object.values(grouped);
  };



  const toggleRowExpansion = useCallback(async (sectorId) => {
    const isCurrentlyExpanded = expandedRows.has(sectorId);

    setExpandedRows(prev => {
      const newExpanded = new Set(prev);
      if (isCurrentlyExpanded) {
        newExpanded.delete(sectorId);
      } else {
        newExpanded.add(sectorId);
      }
      return newExpanded;
    });

    // If expanding, fetch sub-sectors from API
    if (!isCurrentlyExpanded) {
      const sector = sectors.find(s => s.id === sectorId);
      if (sector) {
        // Always fetch fresh data from API when expanding
        const apiSubSectors = await fetchSubSectorsFromAPI(sector.industryId, searchTerm, true, subSectorPagination.pageNumber, subSectorPagination.pageSize);

        if (apiSubSectors.length > 0) {
          setSubSectors(prev => {
            // Remove existing sub-sectors for this industry and add new ones
            const filtered = prev.filter(sub => sub.industryId !== sector.industryId);
            return [...filtered, ...apiSubSectors];
          });
        } else {
          // If no sub-sectors found, still update the state to show empty
          setSubSectors(prev => {
            const filtered = prev.filter(sub => sub.industryId !== sector.industryId);
            return filtered;
          });
        }
      }
    }
  }, [expandedRows, sectors, fetchSubSectorsFromAPI, searchTerm, subSectorPagination.pageNumber, subSectorPagination.pageSize]);

  // Auto-search when search term changes (with debounce)
  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      if (expandedRows.size > 0) {
        // Re-fetch sub-sectors for all expanded industries with new search term
        const expandedIndustryIds = Array.from(expandedRows).map(sectorId => {
          const sector = sectors.find(s => s.id === sectorId);
          return sector ? sector.industryId : null;
        }).filter(Boolean);

        for (const industryId of expandedIndustryIds) {
          const apiSubSectors = await fetchSubSectorsFromAPI(industryId, searchTerm, true, 1, subSectorPagination.pageSize);
          if (apiSubSectors.length > 0) {
            setSubSectors(prev => {
              // Remove existing sub-sectors for this industry and add new ones
              const filtered = prev.filter(sub => sub.industryId !== industryId);
              return [...filtered, ...apiSubSectors];
            });
          }
        }
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchTerm, expandedRows, sectors, fetchSubSectorsFromAPI, subSectorPagination.pageSize]);

  return (
    <DashboardShell
      isMenuCollapsed={isMenuCollapsed}
      sidebar={
        <Menu
          onSelect={handleMenuSelection}
          selectedItem={selectedItem}
          isCollapsed={isMenuCollapsed}
          onToggleCollapse={toggleMenuCollapse}
        />
      }
    >
      <SaListPageShell topBarTitle="Sector Management">
        <div className="dash-frame-parent1">
          <div className="dash-list-toolbar">
            <div className="dash-list-toolbar__intro">
              <h5 className="dash-list-toolbar__title">Manage Sectors</h5>
              <p className="dash-list-toolbar__desc">
                Create and organize industry sectors and their sub-sectors.
              </p>
            </div>
            <div className="dash-list-toolbar__actions">
              <div className="dash-search-field dash-table-search">
                <Search className="dash-search-field__icon" aria-hidden />
                <input
                  type="text"
                  placeholder="Search by name…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="dash-search-field__input"
                  disabled={loading}
                />
              </div>
              <TableSortSelect variant="chip"
                value={sortKey}
                onChange={setSortKey}
                disabled={loading}
              />
              <UiButton variant="primary" className="sa-create-btn" onClick={() => handleShow()}>
                Create Sector
              </UiButton>
            </div>
          </div>

          {loading ? (
            <ContentLoader label="Loading sectors…" />
          ) : error ? (
            <Alert variant="error">{error}</Alert>
          ) : (
            <Fragment>
              <div className="app-table-wrap app-table-wrap--refined">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th aria-label="Expand" />
                      <th>#</th>
                      <th>Industry</th>
                      <th>Code</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getPaginatedSectors().length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center">
                          <span className="app-td__desc">No sectors found.</span>
                        </td>
                      </tr>
                    ) : (
                      getPaginatedSectors().map((sector, index) => {
                        const isExpanded = expandedRows.has(sector.id);
                        const groupedSubSectors = getGroupedSubSectors();
                        const industryGroup = groupedSubSectors.find(
                          (group) => group.industryId === sector.industryId
                        );
                        const displayName = sector.name || "Untitled sector";
                        const rowNumber =
                          (apiPageInfo.pageNumber - 1) * apiPageInfo.pageSize + index + 1;

                        return (
                          <Fragment key={sector.id}>
                            <tr
                              className="rdm-row-clickable"
                              onClick={() => openViewModal(sector, "parent")}
                            >
                              <td onClick={(e) => e.stopPropagation()}>
                                <button
                                  type="button"
                                  className="vd-expand-btn"
                                  onClick={() => toggleRowExpansion(sector.id)}
                                  title={isExpanded ? "Collapse sub-sectors" : "Expand sub-sectors"}
                                  aria-expanded={isExpanded}
                                  aria-label={
                                    isExpanded
                                      ? `Collapse ${displayName}`
                                      : `Expand ${displayName}`
                                  }
                                >
                                  {isExpanded ? <ChevronDown aria-hidden /> : <ChevronRight aria-hidden />}
                                </button>
                              </td>
                              <td>
                                <span className="app-td__index">{rowNumber}</span>
                              </td>
                              <td>
                                <span className="app-td__title">{displayName}</span>
                              </td>
                              <td>
                                {sector.code ? (
                                  <span className="app-td__meta">{sector.code}</span>
                                ) : (
                                  <EmptyCell />
                                )}
                              </td>
                              <td>
                                <span className="app-td__meta">Industry</span>
                              </td>
                              <td>
                                <span
                                  className={
                                    sector.isActive
                                      ? "app-status app-status--success"
                                      : "app-status app-status--muted"
                                  }
                                >
                                  {sector.isActive ? "Active" : "Inactive"}
                                </span>
                              </td>
                              <td
                                className="dash-mobile-table-actions"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div className="btn-list-actions">
                                  <UiButton
                                    variant="outline"
                                    size="sm"
                                    className="btn-list-edit"
                                    title="Edit"
                                    aria-label={`Edit ${displayName}`}
                                    onClick={() => handleShow(sector)}
                                  >
                                    <Pencil />
                                  </UiButton>
                                  <UiButton
                                    variant="danger"
                                    size="sm"
                                    className="btn-list-delete"
                                    title="Delete"
                                    aria-label={`Delete ${displayName}`}
                                    onClick={() => handleDelete(sector)}
                                  >
                                    <Trash2 />
                                  </UiButton>
                                </div>
                              </td>
                            </tr>

                            {isExpanded ? (
                              subSectorsLoading ? (
                                <tr className="vd-sub-row">
                                  <td colSpan={7} className="text-center">
                                    <span className="app-td__desc">Loading sub-sectors…</span>
                                  </td>
                                </tr>
                              ) : industryGroup && industryGroup.subSectors.length > 0 ? (
                                <>
                                  {industryGroup.subSectors.map((subSector) => (
                                    <tr
                                      key={`sub-${subSector.id}`}
                                      className="rdm-row-clickable vd-sub-row"
                                      onClick={() => openViewModal(subSector, "child")}
                                    >
                                      <td />
                                      <td />
                                      <td>
                                        <span className="app-td__desc vd-sub-name">
                                          {subSector.name}
                                        </span>
                                      </td>
                                      <td>
                                        {subSector.code ? (
                                          <span className="app-td__meta">{subSector.code}</span>
                                        ) : (
                                          <EmptyCell />
                                        )}
                                      </td>
                                      <td>
                                        <span className="app-td__meta">Sub-sector</span>
                                      </td>
                                      <td>
                                        <span
                                          className={
                                            subSector.isActive
                                              ? "app-status app-status--success"
                                              : "app-status app-status--muted"
                                          }
                                        >
                                          {subSector.isActive ? "Active" : "Inactive"}
                                        </span>
                                      </td>
                                      <td>
                                        <EmptyCell />
                                      </td>
                                    </tr>
                                  ))}
                                  {subSectorPagination.totalPages > 1 ? (
                                    <tr className="vd-sub-row" onClick={(e) => e.stopPropagation()}>
                                      <td colSpan={7}>
                                        <div className="vd-nested-pagination">
                                          <span className="app-td__meta">
                                            Sub-sectors · page {subSectorPagination.pageNumber} of{" "}
                                            {subSectorPagination.totalPages}
                                          </span>
                                          <UiPagination
                                            page={subSectorPagination.pageNumber}
                                            totalPages={subSectorPagination.totalPages}
                                            onPageChange={goToSubSectorPage}
                                          />
                                        </div>
                                      </td>
                                    </tr>
                                  ) : null}
                                </>
                              ) : (
                                <tr className="vd-sub-row">
                                  <td colSpan={7} className="text-center">
                                    <span className="app-td__desc">
                                      No sub-sectors found for this industry.
                                    </span>
                                  </td>
                                </tr>
                              )
                            ) : null}
                          </Fragment>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              <UiPagination
                page={apiPageInfo.pageNumber}
                totalPages={apiPageInfo.totalPages}
                onPageChange={goToPage}
              />
            </Fragment>
          )}
        </div>
      </SaListPageShell>

      {/* Create/Edit Modal */}
      <Modal
        show={showModal}
        onHide={handleClose}
        centered
        size="lg"
        dialogClassName="rounded-3"
      >
        <Modal.Header closeButton className="dashboard-modal-header">
          <PieChart className="h-5 w-5 me-2 " />
          <Modal.Title>{formData.id ? "Edit Sector" : "Create Sector"}</Modal.Title>
        </Modal.Header>

        <Form onSubmit={handleSubmit}>
          <Modal.Body className="px-4">
            <Row>
              {/* Industry */}
              <Col md={6}>
                <Form.Group controlId="formIndustry" className="mb-2">
                  <Form.Label>Industry <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    name="industryId"
                    value={formData.industryId}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Industry</option>
                    {industries.map(industry => (
                      <option key={industry.id} value={industry.id}>
                        {industry.name} ({industry.code})
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              {/* Name */}
              <Col md={6}>
                <Form.Group controlId="formName" className="mb-2">
                  <Form.Label>Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Enter sector name"
                    maxLength="100"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              {/* Code */}
              <Col md={6}>
                <Form.Group controlId="formCode" className="mb-2">
                  <Form.Label>Code <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="code"
                    value={formData.code}
                    onChange={handleChange}
                    required
                    placeholder="Enter sector code"
                    maxLength="10"
                  />
                </Form.Group>
              </Col>
              {/* IsActive */}
              <Col md={6}>
                <Form.Group controlId="formIsActive" className="mb-0">
                  <Form.Label>Status</Form.Label>
                  <div className="mt-0">
                    <Form.Check
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleChange}
                      label="Active"
                    />
                  </div>
                </Form.Group>
              </Col>
            </Row>

            {/* Sub Sector Creation Section */}
            <div className="d-flex justify-content-between align-items-center mt-2 mb-1">
              <h6 className="mb-0">
                <Factory className="me-2" />
                Add Sub Sectors (Industry Level)
              </h6>
              <Button
                onClick={() => handleAddSubSectorField()}
                className="btn btn-dashboard-primary"
                size="sm"
              >
                <Plus className="me-1" />
                Add More
              </Button>
            </div>

            {/* Sub Sector Fields */}
            {formData.subSectors && formData.subSectors.map((subSector, index) => (
              <div key={subSector.id || index} className="border rounded p-2 mb-2 dashboard-form-section">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0">
                    Sub Sector {index + 1}
                  </h6>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    className="cap-mgmt-icon-btn"
                    onClick={() => handleRemoveSubSectorField(index)}
                  >
                    <Trash2 />
                  </Button>
                </div>
                <Row>
                  <Col md={4}>
                    <Form.Group controlId={`subSectorName${index}`} className="mb-2">
                      <Form.Label>Name <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="text"
                        value={subSector.name}
                        onChange={(e) => handleSubSectorFieldChange(index, 'name', e.target.value)}
                        required
                        placeholder="Enter sub sector name"
                        maxLength="100"
                        style={{ height: '38px' }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group controlId={`subSectorCode${index}`} className="mb-2">
                      <Form.Label>Code <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="text"
                        value={subSector.code}
                        onChange={(e) => handleSubSectorFieldChange(index, 'code', e.target.value)}
                        required
                        placeholder="Enter sub sector code"
                        maxLength="10"
                        style={{ height: '38px' }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group controlId={`subSectorStatus${index}`} className="mb-2">
                      <Form.Label>Status</Form.Label>
                      <Form.Select
                        value={subSector.isActive ? 'true' : 'false'}
                        onChange={(e) => handleSubSectorFieldChange(index, 'isActive', e.target.value === 'true')}
                        style={{ height: '38px' }}
                      >
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group className="mb-2">
                      <Form.Label>&nbsp;</Form.Label>
                      <div className="d-flex align-items-center" style={{ height: '38px' }}>
                        <span className={`badge ${subSector.isActive ? 'bg-success' : 'bg-secondary'}`}>
                          {subSector.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            ))}
          </Modal.Body>

          <Modal.Footer>
            <Button variant="outline-secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              className="btn btn-dashboard-primary"
            >
              Save Sector
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation */}
      <Modal show={showDeleteModal} onHide={cancelDelete} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete{" "}
          {sectorToDelete?.name}?
          {sectorToDelete?.subSectors && sectorToDelete.subSectors.length > 0 && (
            <div className="alert alert-warning mt-2">
              <strong>Warning:</strong> This will also delete all related Sub Sectors for the Industry.
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={cancelDelete}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      <RecordDetailModal
        show={showViewModal}
        onHide={closeViewModal}
        title={viewRecord?.name || (viewMode === "child" ? "Sub Sector Details" : "Sector Details")}
        subtitle={viewMode === "child" ? "Sub sector record" : "Industry / sector record"}
        fields={
          viewMode === "child"
            ? [
                { label: "Name", value: viewRecord?.name },
                { label: "Code", value: viewRecord?.code },
                { label: "Type", value: "Sub Sector" },
                { label: "Active", value: Boolean(viewRecord?.isActive) },
                { label: "Industry", value: viewRecord?.industryName },
              ]
            : [
                { label: "Name", value: viewRecord?.name },
                { label: "Code", value: viewRecord?.code },
                { label: "Type", value: "Industry" },
                { label: "Active", value: Boolean(viewRecord?.isActive) },
              ]
        }
        groups={
          viewMode === "parent"
            ? [
                {
                  title: "Sub Sectors",
                  content: (() => {
                    const children =
                      subSectors.filter(
                        (sub) => sub.industryId === viewRecord?.industryId
                      ) || [];
                    if (!children.length) {
                      return <span className="rdm-empty">None</span>;
                    }
                    return children.map((sub) => (
                      <div key={sub.id} className="rdm-nested-card">
                        <div className="rdm-nested-card-title">{sub.name}</div>
                        <div className="rdm-nested-meta">Code: {sub.code || "—"}</div>
                        <div className="rdm-nested-meta">
                          {sub.isActive ? "Active" : "Inactive"}
                        </div>
                      </div>
                    ));
                  })(),
                },
              ]
            : []
        }
        actions={
          viewMode === "parent"
            ? [
                {
                  type: "edit",
                  onClick: () => {
                    const sector = viewRecord;
                    closeViewModal();
                    if (sector) handleShow(sector);
                  },
                },
                {
                  type: "delete",
                  onClick: () => {
                    const sector = viewRecord;
                    closeViewModal();
                    if (sector) handleDelete(sector);
                  },
                },
              ]
            : []
        }
      />

    </DashboardShell>
  );
};

export default SectorManagement;
